import { Fragment, useMemo } from 'react'
import {
  Box,
  Paper,
  Stack,
  Typography,
  LinearProgress,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material'
import HexagonOutlinedIcon from '@mui/icons-material/HexagonOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts'
import DashboardSearchableSelect from './DashboardSearchableSelect'
import ReportTablePagination from '../SalesReport/ReportTable/ReportTablePagination'
import {
  DASHBOARD_COLORS,
  formatCurrency,
  formatHourLabel,
  formatNumber,
  shortChartLabel,
  dashboardCardSx,
} from './dashboardFormatters'

const PERIOD_OPTIONS = [
  { id: '7d', label: '7 Days' },
  { id: '30d', label: 'Monthly' },
  { id: '90d', label: 'Quarterly' },
]

const EXPIRY_BUCKET_COLORS = {
  7: '#e74c3c',
  15: '#f59300',
  30: '#27ae60',
  60: '#2980b9',
}

const expiryItemStyle = (days, severity) => {
  if (days <= 7 || severity === 'critical') {
    return { dot: '#e74c3c', days: '#c0392b' }
  }
  if (days <= 15 || severity === 'warn') {
    return { dot: '#f59300', days: '#b85c00' }
  }
  return { dot: '#27ae60', days: '#1a7a4a' }
}

const DashboardSectionHeader = ({ icon: Icon, title, children, stackFilters = false }) => (
  <Stack
    direction={stackFilters ? 'column' : 'row'}
    justifyContent="space-between"
    alignItems={stackFilters ? 'stretch' : 'center'}
    flexWrap={stackFilters ? undefined : 'wrap'}
    gap={stackFilters ? 1.25 : 1}
    sx={{ mb: 1.5 }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
      <Box sx={{ color: DASHBOARD_COLORS.red, display: 'flex', alignItems: 'center' }}>
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Typography variant="subtitle1" fontWeight={700} noWrap>{title}</Typography>
    </Stack>
    {children && (
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
        sx={{
          width: stackFilters ? '100%' : undefined,
          flexShrink: 0,
          ml: stackFilters ? undefined : { xs: 0, sm: 'auto' },
          justifyContent: stackFilters ? 'flex-start' : { xs: 'flex-start', sm: 'flex-end' },
        }}
      >
        {children}
      </Stack>
    )}
  </Stack>
)

const sectionPaperSx = dashboardCardSx

export const PendingOrdersSection = ({
  data,
  loading,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  searchValue = '',
  onSearchChange,
}) => {
  const navigate = useNavigate()
  const items = data?.items || []

  return (
    <Paper sx={{ ...sectionPaperSx, overflow: 'hidden', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <DashboardSectionHeader
        icon={AssignmentOutlinedIcon}
        title="Pending Partner Shop Orders"
      />

      <TextField
        size="small"
        fullWidth
        placeholder="Search by shop or item…"
        value={searchValue}
        onChange={(e) => onSearchChange?.(e.target.value)}
        sx={{ mb: 1.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <Stack
        spacing={0}
        divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
      >
        {items.map((item) => (
          <Stack key={item.id} direction="row" alignItems="center" spacing={1.25} sx={{ py: 1.25 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#fff1f3',
                color: DASHBOARD_COLORS.red,
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {item.init}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap>{item.name}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                {item.items}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight={700} sx={{ minWidth: 56, textAlign: 'right' }}>
              {formatCurrency(item.amount)}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(`/orders/${item.id}`)}
              sx={{
                borderColor: DASHBOARD_COLORS.red,
                color: DASHBOARD_COLORS.red,
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 72,
                '&:hover': { borderColor: DASHBOARD_COLORS.red, bgcolor: '#fff5f6' },
              }}
            >
              Review
            </Button>
          </Stack>
        ))}
        {items.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No partner shop orders awaiting acceptance.
          </Typography>
        )}
      </Stack>
      {pagination && (
        <ReportTablePagination
          pagination={pagination}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          compact
          rowsPerPageOptions={[5, 10]}
        />
      )}
    </Paper>
  )
}

export const ExpiryRiskSection = ({
  data,
  filters,
  categories,
  onCategorySearch,
  categoryLoading,
  setFilter,
  loading,
}) => {
  const buckets = (data?.buckets || []).map((b) => ({
    ...b,
    label: `≤${b.days}d`,
    fill: EXPIRY_BUCKET_COLORS[b.days] || DASHBOARD_COLORS.gray,
  }))

  return (
    <Paper sx={{ ...sectionPaperSx, overflow: 'hidden', minWidth: 0, width: '100%', height: '100%' }}>
      <DashboardSectionHeader icon={HexagonOutlinedIcon} title="Expiry Risk" stackFilters>
        <DashboardSearchableSelect
          label="Window"
          options={[
            { id: '7', label: '≤ 7 Days' },
            { id: '15', label: '≤ 15 Days' },
            { id: '30', label: '≤ 30 Days' },
            { id: '60', label: '≤ 60 Days' },
          ]}
          value={filters.expiryWindow}
          onChange={(opt) => setFilter({ expiryWindow: opt?.id || '30' })}
          pill
        />
        <DashboardSearchableSelect
          label="Category"
          options={categories}
          value={filters.expiryCategoryId || filters.categoryId}
          onChange={(opt) => setFilter({ expiryCategoryId: opt?.id || '' })}
          onSearch={onCategorySearch}
          loading={categoryLoading}
          pill
          fullWidth
        />
      </DashboardSectionHeader>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <Box sx={{ height: 110, mb: 1.25 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={buckets.filter((b) => b.count > 0)}
              dataKey="count"
              nameKey="label"
              innerRadius="72%"
              outerRadius="90%"
              paddingAngle={2}
              stroke="#fff"
              strokeWidth={2}
            >
              {buckets.filter((b) => b.count > 0).map((b) => (
                <Cell key={b.days} fill={b.fill} />
              ))}
            </Pie>
            <Tooltip formatter={(v, _n, entry) => [`${v} lots`, entry?.payload?.label]} />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.25} sx={{ mb: 1.5, gap: 1.25 }}>
        {buckets.map((b) => (
          <Stack key={b.days} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: b.fill, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10.5px' }}>
              {b.label}: {formatNumber(b.count)}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Stack spacing={0}>
        {(data?.items || []).map((item, idx) => {
          const style = expiryItemStyle(item.days, item.severity)
          return (
            <Stack
              key={`${item.name}-${item.days}-${idx}`}
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                py: 1,
                borderBottom: idx < (data?.items?.length || 0) - 1 ? `1px solid ${DASHBOARD_COLORS.border}` : 'none',
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: style.dot, flexShrink: 0 }} />
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0, fontSize: '12.5px' }} noWrap title={item.name}>
                {item.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: style.days, fontWeight: 600, fontSize: '11.5px', whiteSpace: 'nowrap' }}
              >
                {item.days} days
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '11px', minWidth: 56, textAlign: 'right', whiteSpace: 'nowrap' }}
              >
                {formatNumber(item.units)} units
              </Typography>
            </Stack>
          )
        })}
        {(data?.items || []).length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 1.5, fontSize: '12px' }}>
            No items expiring in the selected window.
          </Typography>
        )}
      </Stack>
    </Paper>
  )
}

export const FastMoversSection = ({
  data,
  filters,
  categories,
  onCategorySearch,
  categoryLoading,
  setFilter,
  loading,
}) => {
  const chartData = useMemo(() => {
    const labels = data?.labels || []
    const values = data?.values || []
    const max = Math.max(...values, 1)
    return labels.map((label, i) => ({
      label,
      shortLabel: shortChartLabel(label, 16),
      units: values[i] || 0,
      fill: `rgba(208, 2, 27, ${0.35 + (0.65 * (values[i] || 0)) / max})`,
    }))
  }, [data?.labels, data?.values])

  return (
    <Paper sx={{ ...sectionPaperSx, overflow: 'visible', minWidth: 0, width: '100%', height: '100%' }}>
      <DashboardSectionHeader icon={LocalFireDepartmentOutlinedIcon} title="Fast Moving Items" stackFilters>
        <DashboardSearchableSelect
          label="Period"
          options={PERIOD_OPTIONS}
          value={filters.fastPeriod}
          onChange={(opt) => setFilter({ fastPeriod: opt?.id || '7d' })}
          pill
        />
        <DashboardSearchableSelect
          label="Category"
          options={categories}
          value={filters.fastCategoryId || filters.categoryId}
          onChange={(opt) => setFilter({ fastCategoryId: opt?.id || '' })}
          onSearch={onCategorySearch}
          loading={categoryLoading}
          pill
          fullWidth
        />
      </DashboardSectionHeader>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <Box sx={{ height: 240, overflow: 'visible', minWidth: 0 }}>
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 58 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis
              dataKey="shortLabel"
              tick={{ fontSize: 10, fill: '#666' }}
              interval={0}
              angle={-32}
              textAnchor="end"
              height={58}
              tickMargin={8}
              padding={{ left: 24, right: 16 }}
            />
            <YAxis tick={{ fontSize: 10, fill: '#666' }} width={42} />
            <Tooltip
              formatter={(v) => [`${formatNumber(v)} units`, 'Sold']}
              labelFormatter={(_l, payload) => payload?.[0]?.payload?.label || _l}
            />
            <Bar dataKey="units" radius={[4, 4, 0, 0]} maxBarSize={48}>
              {chartData.map((entry) => (
                <Cell key={entry.label} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      {chartData.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          No fast-moving items for this period.
        </Typography>
      )}
    </Paper>
  )
}

export const PeakHoursHeatmap = ({
  data,
  filters,
  locations,
  onLocationSearch,
  locationLoading,
  setFilter,
  loading,
}) => {
  const weekdays = data?.weekdays || []
  const colCount = Math.max(weekdays.length, 1)
  const heatColors = ['#fce8ea', '#f5b8be', '#ea8a93', '#e05c6a', '#d42f40', '#D0021B', '#a80016']
  const legendColors = ['#fce8ea', '#f5b8be', '#e87480', '#D0021B']
  const peak = data?.peak
  const isAvg = filters.heatmapWeek === 'avg'
  const countUnit = isAvg ? 'avg orders' : 'orders'

  return (
    <Paper sx={sectionPaperSx}>
      <DashboardSectionHeader icon={ScheduleOutlinedIcon} title="Peak Hours Heatmap">
        <DashboardSearchableSelect
          label="Outlet"
          options={locations}
          value={filters.heatmapOutletId || filters.locationId}
          onChange={(opt) => setFilter({ heatmapOutletId: opt?.id || '' })}
          onSearch={onLocationSearch}
          loading={locationLoading}
          pill
        />
        <DashboardSearchableSelect
          label="This Week"
          options={[
            { id: 'this', label: 'This Week' },
            { id: 'last', label: 'Last Week' },
            { id: 'avg', label: 'Monthly Avg' },
          ]}
          value={filters.heatmapWeek}
          onChange={(opt) => setFilter({ heatmapWeek: opt?.id || 'this' })}
          pill
        />
      </DashboardSectionHeader>

      {data?.rangeLabel && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Partner order fulfillment times · {data.rangeLabel}
        </Typography>
      )}

      {peak?.day && peak.count > 0 && (
        <Box
          sx={{
            mb: 1.25,
            px: 1.25,
            py: 0.75,
            borderRadius: '8px',
            bgcolor: '#fff1f3',
            border: `1px solid ${DASHBOARD_COLORS.border}`,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: DASHBOARD_COLORS.red }}>
            Busiest slot: {peak.day} {peak.hourLabel}
            {' · '}
            {Number.isInteger(peak.count) ? peak.count : peak.count.toFixed(1)} {countUnit}
          </Typography>
        </Box>
      )}

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `48px repeat(${colCount}, minmax(36px, 1fr))`,
          gap: 0.75,
          overflowX: 'auto',
        }}
      >
        <Box />
        {weekdays.map((d) => (
          <Typography
            key={d}
            variant="caption"
            align="center"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {d}
          </Typography>
        ))}
        {(data?.hours || []).map((hour, hi) => (
          <Fragment key={`hour-row-${hour}`}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: 'right', pr: 0.5, whiteSpace: 'nowrap', fontSize: '10px', alignSelf: 'center' }}
            >
              {formatHourLabel(hour)}
            </Typography>
            {weekdays.map((day, wi) => {
              const intensity = data?.grid?.[hi]?.[wi] || 0
              const rawCount = data?.counts?.[hi]?.[wi] || 0
              const showCount = rawCount > 0
              return (
                <Box
                  key={`${hour}-${day}`}
                  title={`${day} ${formatHourLabel(hour)}: ${showCount ? `${rawCount} ${countUnit}` : 'No orders'}`}
                  sx={{
                    height: 32,
                    minWidth: 36,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: heatColors[Math.min(intensity, heatColors.length - 1)],
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: intensity >= 7 ? 'inset 0 0 0 1px rgba(255,255,255,0.35)' : 'none',
                  }}
                >
                  {showCount && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: intensity >= 4 ? '#fff' : '#555550',
                        lineHeight: 1,
                      }}
                    >
                      {Number.isInteger(rawCount) ? rawCount : rawCount.toFixed(1)}
                    </Typography>
                  )}
                </Box>
              )
            })}
          </Fragment>
        ))}
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="caption" color="text.secondary">Low</Typography>
          <Stack direction="row" spacing={0.25}>
            {legendColors.map((color, i) => (
              <Box
                key={color}
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: 0.75,
                  bgcolor: color,
                  border: i === 0 ? `1px solid ${DASHBOARD_COLORS.border}` : 'none',
                }}
              />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">High</Typography>
        </Stack>
        {data?.maxCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            Max: {Number.isInteger(data.maxCount) ? data.maxCount : data.maxCount.toFixed(1)} {countUnit}
          </Typography>
        )}
      </Stack>
    </Paper>
  )
}

export const QuickActionsSection = () => {
  const actions = [
    { label: 'Upload Product', path: '/products/upload', icon: CloudUploadOutlinedIcon },
    { label: 'Manage Inventory', path: '/inventory/manage', icon: InventoryOutlinedIcon },
    { label: 'Approve Orders', path: '/orders/purchase', icon: AssignmentTurnedInOutlinedIcon },
    { label: 'Manage Promos', path: '/promotions/manage', icon: LocalOfferOutlinedIcon },
  ]
  return (
    <Paper sx={{ ...sectionPaperSx, boxShadow: 'none', border: 'none', bgcolor: 'transparent', p: 0 }}>
      <DashboardSectionHeader icon={BoltOutlinedIcon} title="Quick Actions" />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.25 }}>
        {actions.map((a) => {
          const Icon = a.icon
          return (
            <Box
              key={a.label}
              component={RouterLink}
              to={a.path}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                p: 1.75,
                bgcolor: '#fff',
                border: `1px solid ${DASHBOARD_COLORS.border}`,
                borderRadius: '12px',
                cursor: 'pointer',
                color: '#555550',
                textDecoration: 'none',
                transition: 'all 0.15s',
                '&:hover': {
                  bgcolor: '#fff1f3',
                  borderColor: '#ffe0e4',
                  color: DASHBOARD_COLORS.red,
                  '& .qa-icon-wrap': { bgcolor: '#ffe0e4', color: DASHBOARD_COLORS.red },
                },
              }}
            >
              <Box
                className="qa-icon-wrap"
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  bgcolor: DASHBOARD_COLORS.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s',
                }}
              >
                <Icon sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="caption" fontWeight={600} align="center">{a.label}</Typography>
            </Box>
          )
        })}
      </Box>
    </Paper>
  )
}
